import { DialogOpenEvent } from '@theme/dialog';

/**
 * Mismo placeholder en el modal que en el botón del header (setting del customizer).
 */
export function syncOffsiderSearchPlaceholder() {
  const header = document.getElementById('header-component');
  const label = document.querySelector('.header__offsider-search .offsider-search-button__placeholder');
  const text =
    label?.textContent?.trim() ||
    header?.getAttribute('data-offsider-search-placeholder') ||
    document
      .querySelector('.header__offsider-search .search-modal__button[data-offsider-search-placeholder]')
      ?.getAttribute('data-offsider-search-placeholder');

  if (!text) return;

  const input = document.querySelector('#search-modal .search-input[data-offsider-search-input]');
  if (input instanceof HTMLInputElement) {
    input.placeholder = text;
  }
}

/**
 * Megamenú Offsider: apertura por clic, cierre con X / Escape / clic fuera.
 * Buscador header: 1.er clic abre megamenú; 2.º clic (megamenú abierto) abre `#search-modal`.
 * Buscador del panel (`data-offsider-megamenu-search`, móvil) abre el modal.
 */
class OffsiderMegamenuPanel extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelectorAll('[data-category]').forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          const categoryButton = /** @type {HTMLButtonElement} */ (button);
          const categoryUrl = categoryButton.dataset.categoryUrl;
          if (categoryUrl) {
            window.location.assign(categoryUrl);
            return;
          }
          const handle = categoryButton.dataset.category;
          if (handle) this.setCategory(handle);
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {string} handle
   */
  setCategory(handle) {
    this.querySelectorAll('[data-category]').forEach((button) => {
      const active = /** @type {HTMLButtonElement} */ (button).dataset.category === handle;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    this.querySelectorAll('[data-category-panel]').forEach((panel) => {
      const active = /** @type {HTMLElement} */ (panel).dataset.categoryPanel === handle;
      panel.classList.toggle('is-active', active);
      if (active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }
}

if (!customElements.get('offsider-megamenu-panel')) {
  customElements.define('offsider-megamenu-panel', OffsiderMegamenuPanel);
}

class OffsiderMegamenuController {
  /** @type {HTMLElement | null} */
  #openItem = null;

  /** @type {AbortController} */
  #abort;

  /**
   * @param {HTMLElement} header
   * @param {HTMLElement} headerMenu
   */
  constructor(header, headerMenu) {
    this.#header = header;
    this.#headerMenu = headerMenu;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.querySelectorAll('.menu-list__link--offsider-megamenu').forEach((link) => {
      link.addEventListener('click', (event) => this.#onTriggerClick(event), { signal });
    });

    document.querySelectorAll('[data-offsider-megamenu-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }, { signal });
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.close();
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#openItem) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('.menu-list__list-item--offsider-megamenu')) return;
        if (target.closest('.menu-list__submenu--offsider')) return;
        if (target.closest('.header__offsider-search')) return;
        if (target.closest('#search-modal')) return;
        this.close();
      },
      { signal }
    );

    this.#setupSearchIntegration(signal);
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupSearchIntegration(signal) {
    const searchRoot = document.querySelector('.header__offsider-search');
    searchRoot?.addEventListener('click', (event) => this.#onHeaderSearchClick(event), { signal, capture: true });

    document.addEventListener(
      'click',
      (event) => {
        const trigger =
          event.target instanceof Element ? event.target.closest('[data-offsider-megamenu-search]') : null;
        if (!trigger) return;
        this.#onMegamenuSearchClick(event);
      },
      { signal }
    );

    const searchModal = document.getElementById('search-modal');
    searchModal?.addEventListener(DialogOpenEvent.eventName, () => this.close(), { signal });
  }

  /**
   * @param {Event} event
   */
  #onHeaderSearchClick(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-offsider-header-search]') : null;
    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#openItem) {
      if (this.#isMobileMegamenu()) {
        this.close();
        return;
      }

      this.close();
      void this.#openSearchModal();
      return;
    }

    this.#openDefaultMegamenu();
  }

  #isMobileMegamenu() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  #promoteMegamenuItemsToMainList() {
    document.querySelectorAll('.menu-list__list-item--offsider-megamenu').forEach((item) => {
      if (item instanceof HTMLElement) {
        item.removeAttribute('slot');
        item.slot = '';
      }
    });

    if (this.#openItem instanceof HTMLElement) {
      this.#openItem.removeAttribute('slot');
      this.#openItem.slot = '';
    }
  }

  #prepareMobileMegamenuLayout() {
    if (!this.#isMobileMegamenu()) return;

    const nav = this.#header.querySelector('.header__offsider-nav');
    if (nav instanceof HTMLElement) {
      nav.style.width = '100%';
      nav.style.maxWidth = '100%';
    }

    const overflowList = this.#headerMenu.querySelector('overflow-list');
    if (overflowList instanceof HTMLElement) {
      overflowList.removeAttribute('disabled');
      if (typeof overflowList.showAll === 'function') {
        overflowList.showAll();
      }
    }

    this.#promoteMegamenuItemsToMainList();
  }

  #restoreMobileOverflow() {
    if (!this.#isMobileMegamenu()) return;

    const nav = this.#header.querySelector('.header__offsider-nav');
    if (nav instanceof HTMLElement) {
      nav.style.removeProperty('width');
      nav.style.removeProperty('max-width');
    }

    const overflowList = this.#headerMenu.querySelector('overflow-list');
    if (!(overflowList instanceof HTMLElement)) return;

    overflowList.removeAttribute('disabled');
    overflowList.dispatchEvent(new CustomEvent('reflow', { bubbles: true }));
  }

  /**
   * @param {Event} event
   */
  #onMegamenuSearchClick(event) {
    event.preventDefault();
    event.stopPropagation();

    this.close();
    void this.#openSearchModal();
  }

  async #openSearchModal() {
    const searchModal = document.getElementById('search-modal');
    if (!searchModal) return;

    syncOffsiderSearchPlaceholder();

    await customElements.whenDefined('dialog-component');

    requestAnimationFrame(() => {
      if (typeof searchModal.showDialog === 'function') {
        searchModal.showDialog();
      } else {
        const dialog = searchModal.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && !dialog.open) {
          dialog.showModal();
        }
      }
      syncOffsiderSearchPlaceholder();
    });
  }

  #openDefaultMegamenu() {
    const firstLink = document.querySelector('.menu-list__link--offsider-megamenu');
    const listItem = firstLink?.closest('.menu-list__list-item--offsider-megamenu');

    if (!(firstLink instanceof HTMLAnchorElement) || !(listItem instanceof HTMLElement)) return;

    this.open(listItem, firstLink);

    const searchButton = document.querySelector('[data-offsider-header-search]');
    if (searchButton instanceof HTMLButtonElement) {
      searchButton.setAttribute('aria-expanded', 'true');
    }
  }

  /** @type {HTMLElement} */
  #header;

  /** @type {HTMLElement} */
  #headerMenu;

  /**
   * @param {Event} event
   */
  #onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const listItem = link.closest('.menu-list__list-item--offsider-megamenu');
    if (!(listItem instanceof HTMLElement)) return;

    if (this.#openItem === listItem && link.getAttribute('aria-expanded') === 'true') {
      this.close();
      return;
    }

    this.close();
    this.open(listItem, link);
  }

  /**
   * @param {HTMLElement} listItem
   * @param {HTMLAnchorElement} link
   */
  open(listItem, link) {
    const submenu = listItem.querySelector('.menu-list__submenu--offsider');
    const panel = listItem.querySelector('offsider-megamenu-panel');
    const triggerHandle = link.dataset.offsiderMegamenuTrigger;

    this.#openItem = listItem;
    link.setAttribute('aria-expanded', 'true');
    listItem.classList.add('menu-list__list-item--offsider-open');
    this.#header.classList.add('header--offsider-megamenu-open');
    document.body.classList.add('offsider-megamenu-open');

    this.#headerMenu.querySelectorAll('[aria-expanded="true"]').forEach((expanded) => {
      if (expanded !== link) expanded.setAttribute('aria-expanded', 'false');
    });

    if (panel instanceof OffsiderMegamenuPanel && triggerHandle) {
      panel.setCategory(triggerHandle);
    }

    if (this.#isMobileMegamenu()) {
      this.#prepareMobileMegamenuLayout();
    }

    requestAnimationFrame(() => {
      if (this.#isMobileMegamenu()) {
        this.#prepareMobileMegamenuLayout();
      }

      requestAnimationFrame(() => {
        let height = 0;
        if (submenu instanceof HTMLElement) {
          height = submenu.scrollHeight;
          if (this.#isMobileMegamenu() && height < 1) {
            height = Math.round(window.innerHeight * 0.85);
          }
        }

        this.#headerMenu.style.setProperty('--submenu-height', `${height}px`);
        this.#headerMenu.style.setProperty('--submenu-opacity', '1');
        submenu?.setAttribute('data-active', '');
      });
    });
  }

  close() {
    if (!this.#openItem) return;

    const link = this.#openItem.querySelector('.menu-list__link--offsider-megamenu');
    const submenu = this.#openItem.querySelector('.menu-list__submenu--offsider');

    link?.setAttribute('aria-expanded', 'false');
    this.#openItem.classList.remove('menu-list__list-item--offsider-open');
    this.#openItem = null;

    this.#header.classList.remove('header--offsider-megamenu-open');
    document.body.classList.remove('offsider-megamenu-open');

    this.#headerMenu.style.setProperty('--submenu-height', '0px');
    this.#headerMenu.style.setProperty('--submenu-opacity', '0');
    submenu?.removeAttribute('data-active');

    this.#restoreMobileOverflow();

    const searchButton = document.querySelector('[data-offsider-header-search]');
    if (searchButton instanceof HTMLButtonElement) {
      searchButton.setAttribute('aria-expanded', 'false');
    }
  }

  destroy() {
    this.close();
    this.#abort.abort();
  }
}

/** @type {OffsiderMegamenuController | null} */
let megamenuController = null;

function initOffsiderSearchPlaceholder() {
  const searchModal = document.getElementById('search-modal');
  if (!searchModal) return;

  searchModal.addEventListener(DialogOpenEvent.eventName, () => {
    syncOffsiderSearchPlaceholder();
  });

  syncOffsiderSearchPlaceholder();
}

function initOffsiderMegamenu() {
  const header = document.getElementById('header-component');
  const headerMenu =
    header?.querySelector('.header__offsider-nav header-menu') ?? document.querySelector('header-menu');

  if (!header || !(headerMenu instanceof HTMLElement)) return;
  if (!document.querySelector('.menu-list__link--offsider-megamenu')) return;

  megamenuController?.destroy();
  megamenuController = new OffsiderMegamenuController(header, headerMenu);
}

function initOffsiderHeaderScripts() {
  if (document.querySelector('.header__offsider-search')) {
    initOffsiderSearchPlaceholder();
  }

  initOffsiderMegamenu();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOffsiderHeaderScripts, { once: true });
} else {
  initOffsiderHeaderScripts();
}
